'use client';

import {
  ActionIcon,
  Anchor,
  Center,
  Group,
  Rating,
  Table,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
  keys,
} from '@mantine/core';
import {
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconListCheck,
  IconPlus,
  IconSearch,
  IconSelector,
} from '@tabler/icons-react';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import { useState } from 'react';
import useSWR from 'swr';

interface ThProps {
  children: React.ReactNode;
  reversed: boolean;
  sorted: boolean;
  onSort: () => void;
}

function Th({ children, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;
  return (
    <Table.Th>
      <UnstyledButton onClick={onSort}>
        <Group justify='space-between' style={{ flexWrap: 'nowrap' }}>
          <Text fw={500} fz='sm'>
            {children}
          </Text>
          <Center>
            <Icon size='0.9rem' stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </Table.Th>
  );
}

function filterData(data: Climb[], search: string) {
  const query = search.toLowerCase().trim();
  return data.filter((item) =>
    keys(data[0]).some((key) =>
      String(item[key]).toLowerCase().includes(query),
    ),
  );
}

function sortData(
  data: Climb[],
  payload: {
    sortBy: keyof Climb | null;
    reversed: boolean;
    search: string;
  },
) {
  const { sortBy } = payload;

  if (!sortBy) {
    return filterData(data, payload.search);
  }

  return filterData(
    [...data].sort((a, b) => {
      if (payload.reversed) {
        return String(b[sortBy]).localeCompare(String(a[sortBy]), 'en', {
          numeric: true,
        });
      }

      return String(a[sortBy]).localeCompare(String(b[sortBy]), 'en', {
        numeric: true,
      });
    }),
    payload.search,
  );
}

export default function ClimbsTable({ climbs, crag, discipline }) {
  const { data: session } = useSession();
  const { data: userAscents } = useSWR(
    () =>
      session &&
      `/api/users/${session.user.username}/${discipline}/${crag.nameSlug}`,
  );

  const [search, setSearch] = useState('');
  const [sortedData, setSortedData] = useState(climbs);
  const [sortBy, setSortBy] = useState<keyof Climb | null>('name');
  const [reverseSortDirection, setReverseSortDirection] = useState(false);

  const checkComplete = (ascents, climb) =>
    ascents?.find((a) => a.climb._id === climb._id);

  const setSorting = (field: keyof Climb) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(climbs, { sortBy: field, reversed, search }));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    setSearch(value);
    setSortedData(
      sortData(climbs, {
        sortBy,
        reversed: reverseSortDirection,
        search: value,
      }),
    );
  };

  const rows = sortedData.map((row) => (
    <Table.Tr key={row._id}>
      {session && (
        <Table.Td>
          <Center>
            {checkComplete(userAscents, row) ? (
              <Tooltip label='Edit ascent'>
                <ActionIcon
                  size='xs'
                  variant='subtle'
                  color='green'
                  component={Link}
                  href={`/ascents/${checkComplete(userAscents, row)._id}`}
                >
                  <IconCheck color='var(--mantine-color-green-filled)' />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Tooltip label='add climb'>
                <ActionIcon
                  size='xs'
                  variant='subtle'
                  component={Link}
                  href={`/ascents/add?climb=${row.nameSlug}`}
                >
                  <IconPlus />
                </ActionIcon>
              </Tooltip>
            )}
          </Center>
        </Table.Td>
      )}
      <Table.Td>
        <Anchor fz='sm' c='inherit' href={`/climbs/${row.nameSlug}`}>
          {row.name}
        </Anchor>
      </Table.Td>
      <Table.Td>{row.sector}</Table.Td>
      <Table.Td>{row.grade}</Table.Td>
      <Table.Td>{row.ascents}</Table.Td>
      <Table.Td>
        {row.rating && <Rating value={row.rating} size='xs' readOnly />}
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <TextInput
        placeholder='Search by any field'
        my='md'
        leftSection={<IconSearch size='0.9rem' stroke={1.5} />}
        value={search}
        onChange={handleSearchChange}
      />
      <Table.ScrollContainer h={600} minWidth={600}>
        <Table
          highlightOnHover
          verticalSpacing='xs'
          horizontalSpacing='xs'
          stickyHeader
          styles={{ thead: { zIndex: 1 } }}
        >
          <Table.Thead>
            <Table.Tr>
              {session && (
                <Table.Th>
                  <IconListCheck size={16} />
                </Table.Th>
              )}
              <Th
                sorted={sortBy === 'name'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('name')}
              >
                Name
              </Th>
              <Th
                sorted={sortBy === 'sector'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('sector')}
              >
                Sector
              </Th>
              <Th
                sorted={sortBy === 'grade'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('grade')}
              >
                Grade
              </Th>
              <Th
                sorted={sortBy === 'ascents'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('ascents')}
              >
                Ascents
              </Th>
              <Th
                sorted={sortBy === 'rating'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('rating')}
              >
                Rating
              </Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.length > 0 ? (
              rows
            ) : (
              <Table.Tr>
                <Table.Td colSpan={Object.keys(climbs[0]).length}>
                  <Text fw={500} ta='center'>
                    Nothing found
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </>
  );
}
